import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Profile from './Profile';
import { useTheme } from '../contexts/ThemeContext';
import { useClerk } from '@clerk/clerk-react';
import { Settings as SettingsIcon, Moon, Sun, LogOut, Monitor, Check, X, Wand2 } from 'lucide-react';
import CreateThemeModal from './CreateThemeModal';

const ThemeCard = ({ themeName, themeValues, isActive, isSelected, onClick }) => {
  const { deleteTheme } = useTheme();
  const isCustomTheme = themeValues.category !== undefined;

  const handleDelete = (e) => {
    e.stopPropagation();
    if (isCustomTheme) {
      deleteTheme(themeValues.name, themeValues.category);
    }
  };

  return (
    <button
      onClick={onClick}
      className={`
        w-full p-3 rounded-lg border transition-all duration-200 relative touch-manipulation
        ${isActive || isSelected ? 'border-primary' : 'border-theme hover:border-primary'}
      `}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-theme capitalize truncate">
          {themeName.replace(/([A-Z])/g, ' $1').trim()}
        </h3>
        <div className="flex gap-2 flex-shrink-0">
          {isSelected && (
            <span className="text-[10px] bg-primary text-theme px-1.5 py-0.5 rounded-full">
              Selected
            </span>
          )}
          {isActive && !isSelected && (
            <span className="text-[10px] bg-secondary text-theme px-1.5 py-0.5 rounded-full">
              Active
            </span>
          )}
          {isCustomTheme && (
            <button
              onClick={handleDelete}
              className="text-[10px] bg-error text-theme hover:bg-opacity-80 px-1.5 py-0.5 rounded-full transition-colors"
            >
              Delete
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {Object.entries(themeValues).map(([key, value]) => {
          if (key !== 'name' && key !== 'category') {
            return (
              <div
                key={key}
                className="aspect-square rounded"
                style={{ backgroundColor: value }}
                title={`${key}: ${value}`}
              />
            );
          }
          return null;
        })}
      </div>
    </button>
  );
};

const ThemeSection = ({ title, themes, currentTheme, selectedTheme, onThemeSelect }) => {
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-medium text-theme">{title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(themes).map(([themeName, themeValues]) => (
          <ThemeCard
            key={themeName}
            themeName={themeName}
            themeValues={themeValues}
            isActive={currentTheme.name === themeValues.name}
            isSelected={selectedTheme?.name === themeValues.name}
            onClick={() => onThemeSelect(themeValues)}
          />
        ))}
      </div>
    </div>
  );
};

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isSettingDefaults, setIsSettingDefaults] = useState(false);
  const [selectedDefaults, setSelectedDefaults] = useState({ light: null, dark: null });
  const [isCreateThemeModalOpen, setIsCreateThemeModalOpen] = useState(false);
  const themeContext = useTheme();

  console.log('Settings received theme context:', themeContext);

  const { signOut } = useClerk();
  const navigate = useNavigate();

  const handleLogout = () => {
    signOut().then(() => {
      navigate('/sign-in');
    });
  };

  // Destructure theme context after checking
  const { theme, setTheme, themes, isSystemTheme, defaultLight, defaultDark, setDefaultTheme } = themeContext || {};

  const handleThemeSelect = (newTheme) => {
    if (isSettingDefaults) {
      // When setting defaults, update the selected defaults
      const category = Object.entries(themes).find(([_, categoryThemes]) =>
        Object.values(categoryThemes).some(t => t.name === newTheme.name)
      )?.[0];
      
      setSelectedDefaults(prev => ({
        ...prev,
        [category]: newTheme
      }));
    } else {
      // Normal theme selection
      setTheme(newTheme);
    }
  };

  const handleConfirmDefaults = () => {
    if (selectedDefaults.light) setDefaultTheme('light', selectedDefaults.light);
    if (selectedDefaults.dark) setDefaultTheme('dark', selectedDefaults.dark);
    setIsSettingDefaults(false);
    setSelectedDefaults({ light: null, dark: null });
    // Ensure system theme is active after setting defaults
    setTheme('system');
  };

  const handleCancelDefaults = () => {
    setIsSettingDefaults(false);
    setSelectedDefaults({ light: null, dark: null });
  };

  // Add more detailed loading state
  if (!themeContext) {
    console.log('No theme context available');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!theme) {
    console.log('No theme available in context:', themeContext);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!themes) {
    console.log('No themes available in context:', themeContext);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  console.log('Settings rendering with theme:', theme);

  return (
    <div className="min-h-screen bg-theme theme-transition">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Mobile Header with Title */}
        <div className="md:hidden">
          <h1 className="text-xl font-bold text-theme mb-4">Settings</h1>
          
          {/* Mobile Tab Navigation */}
          <div className="flex overflow-x-auto space-x-2 pb-4 mb-4 border-b border-theme">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex items-center px-4 py-2 rounded-lg whitespace-nowrap ${
                activeTab === 'profile' 
                ? 'bg-primary text-theme' 
                : 'bg-secondary text-accent hover:bg-primary/80 hover:text-theme'
              }`}
            >
              <SettingsIcon size={18} className="mr-2" />
              Profile
            </button>
            <button
              onClick={() => setActiveTab('appearance')}
              className={`flex items-center px-4 py-2 rounded-lg whitespace-nowrap ${
                activeTab === 'appearance' 
                ? 'bg-primary text-theme' 
                : 'bg-secondary text-accent hover:bg-primary/80 hover:text-theme'
              }`}
            >
              {theme?.name === 'default' ? <Sun size={18} className="mr-2" /> : <Moon size={18} className="mr-2" />}
              Appearance
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center px-4 py-2 rounded-lg bg-secondary text-accent hover:bg-primary/80 hover:text-theme whitespace-nowrap"
            >
              <LogOut size={18} className="mr-2" />
              Logout
            </button>
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row gap-4 md:gap-8">
          {/* Settings Sidebar - Desktop Only */}
          <div className="hidden md:block w-64 shrink-0">
            <div className="sticky top-8">
              <div className="p-4 rounded-lg border border-theme bg-theme theme-transition">
                <h2 className="text-lg font-semibold text-theme mb-4">Settings</h2>
                <nav className="space-y-2">
                  <button
                    onClick={() => setActiveTab('profile')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left ${
                      activeTab === 'profile' ? 'bg-primary text-theme' : 'text-accent hover:bg-secondary'
                    }`}
                  >
                    <SettingsIcon size={18} />
                    Profile Settings
                  </button>
                  <button
                    onClick={() => setActiveTab('appearance')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left ${
                      activeTab === 'appearance' ? 'bg-primary text-theme' : 'text-accent hover:bg-secondary'
                    }`}
                  >
                    {theme?.name === 'default' ? <Sun size={18} /> : <Moon size={18} />}
                    Appearance
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-accent hover:bg-secondary"
                  >
                    <LogOut size={18} />
                    Logout
                  </button>
                </nav>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {activeTab === 'profile' && <Profile />}
            {activeTab === 'appearance' && (
              <div className="space-y-8">
                <div className="p-4 sm:p-6 rounded-lg border border-theme bg-theme theme-transition">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                    <div>
                      <h2 className="text-lg font-semibold text-theme">Theme</h2>
                      <p className="text-sm text-accent">Choose your preferred theme</p>
                    </div>
                    <button
                      onClick={() => setIsCreateThemeModalOpen(true)}
                      className="px-4 py-2 bg-primary text-theme rounded-lg hover:bg-primaryHover transition-colors flex items-center justify-center gap-2 w-full sm:w-auto"
                    >
                      <Wand2 size={18} />
                      Create Theme
                    </button>
                  </div>

                  {/* System Theme Card */}
                  <div className="mb-8">
                    <div className={`
                      p-4 rounded-lg border transition-all duration-200
                      ${isSystemTheme ? 'border-primary' : 'border-theme'}
                    `}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                        <div className="flex items-center gap-3">
                          <Monitor size={20} className="text-theme flex-shrink-0" />
                          <div>
                            <h3 className="font-medium text-theme">Match System Theme</h3>
                            <p className="text-sm text-accent">Automatically switch between light and dark themes</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setTheme(isSystemTheme ? defaultLight : 'system')}
                          className={`
                            px-3 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 w-full sm:w-auto
                            ${isSystemTheme 
                              ? 'bg-primary text-theme' 
                              : 'bg-secondary text-accent hover:bg-primary hover:text-theme'
                            }
                          `}
                        >
                          {isSystemTheme ? 'On' : 'Off'}
                        </button>
                      </div>

                      {isSystemTheme && !isSettingDefaults && (
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between px-3 py-2 rounded bg-secondary">
                          <div className="text-sm">
                            <span className="text-theme font-medium">Current Defaults: </span>
                            <span className="text-accent">
                              Light: {defaultLight?.name.replace(/([A-Z])/g, ' $1').trim()}, 
                              Dark: {defaultDark?.name.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                          </div>
                          <button
                            onClick={() => setIsSettingDefaults(true)}
                            className="px-3 py-1.5 mt-2 sm:mt-0 text-sm bg-primary text-theme rounded-full hover:bg-primaryHover transition-colors duration-200"
                          >
                            Set Defaults
                          </button>
                        </div>
                      )}

                      {isSettingDefaults && (
                        <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between px-3 py-2 rounded bg-secondary">
                          <div className="text-sm text-theme mb-2 sm:mb-0">
                            <span className="font-medium">Select your default themes</span>
                            {(!selectedDefaults.light || !selectedDefaults.dark) && (
                              <span className="text-accent ml-2 block sm:inline">
                                {!selectedDefaults.light && !selectedDefaults.dark 
                                  ? "Select both light and dark themes"
                                  : !selectedDefaults.light 
                                    ? "Select a light theme"
                                    : "Select a dark theme"
                                }
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={handleCancelDefaults}
                              className="p-1.5 text-accent hover:text-theme hover:bg-primary rounded-full transition-colors duration-200"
                            >
                              <X size={16} />
                            </button>
                            <button
                              onClick={handleConfirmDefaults}
                              disabled={!selectedDefaults.light || !selectedDefaults.dark}
                              className={`
                                p-1.5 rounded-full transition-colors duration-200
                                ${(!selectedDefaults.light || !selectedDefaults.dark)
                                  ? 'text-accent bg-secondary cursor-not-allowed'
                                  : 'text-theme bg-primary hover:bg-primaryHover'
                                }
                              `}
                            >
                              <Check size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-8">
                    <ThemeSection
                      title="Light Themes"
                      themes={themes.light}
                      currentTheme={theme}
                      selectedTheme={isSettingDefaults ? selectedDefaults.light : null}
                      onThemeSelect={handleThemeSelect}
                    />
                    <ThemeSection
                      title="Dark Themes"
                      themes={themes.dark}
                      currentTheme={theme}
                      selectedTheme={isSettingDefaults ? selectedDefaults.dark : null}
                      onThemeSelect={handleThemeSelect}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <CreateThemeModal
        isOpen={isCreateThemeModalOpen}
        onClose={() => setIsCreateThemeModalOpen(false)}
      />
    </div>
  );
};

export default Settings; 